import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { apiClient } from '../../../utils/api-client';

interface SenderRow {
  ascentLogId: string;
  username: string;
  slug: string;
  avatarUrl: string | null;
  sendType: string | null;
  attemptsCount: number | null;
  sentAt: string | null;
}

interface ClimbSendersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  climbSlugOrId: string;
}

interface RawSendItem {
  attributes: {
    ascent_log_id?: string;
    send_type?: string | null;
    attempts_count?: number | null;
    sent_at?: string | null;
    user: {
      username: string;
      slug: string;
      avatar_small_url?: string | null;
    };
  };
}

export function ClimbSendersDrawer({ open, onOpenChange, climbSlugOrId }: ClimbSendersDrawerProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<SenderRow[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    apiClient
      .get(`/api/climbs/${climbSlugOrId}/sends`)
      .then(res => res.json())
      .then((json: { data: RawSendItem[] }) => {
        if (cancelled) return;
        const mapped: SenderRow[] = (json.data ?? [])
          .filter(item => item.attributes.sent_at)
          .map(item => ({
            ascentLogId: item.attributes.ascent_log_id ?? '',
            username: item.attributes.user.username,
            slug: item.attributes.user.slug,
            avatarUrl: item.attributes.user.avatar_small_url ?? null,
            sendType: item.attributes.send_type ?? null,
            attemptsCount: item.attributes.attempts_count ?? null,
            sentAt: item.attributes.sent_at ?? null,
          }))
          .sort((a, b) => (b.sentAt ?? '').localeCompare(a.sentAt ?? ''));
        setRows(mapped);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, climbSlugOrId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('home.instagram.sendsTitle')}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              {t('home.instagram.noSends')} {t('home.instagram.beTheFirst')}
            </div>
          ) : (
            rows.map(row => (
              <Link
                key={row.ascentLogId}
                to={`/users/${row.slug}`}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors"
              >
                <Avatar className="h-9 w-9">
                  {row.avatarUrl ? <AvatarImage src={row.avatarUrl} alt={row.username} /> : null}
                  <AvatarFallback className="text-xs">
                    {row.username.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{row.username}</div>
                  {row.sendType === 'worked' && row.attemptsCount ? (
                    <div className="text-xs text-muted-foreground">
                      {t('home.instagram.attemptsCount', { count: row.attemptsCount })}
                    </div>
                  ) : null}
                </div>
                {row.sendType ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-foreground/80 whitespace-nowrap">
                    {t(`home.instagram.sendType.${row.sendType}`, row.sendType)}
                  </span>
                ) : null}
              </Link>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
